/**
 * Unwraps the backend's success envelope into `{ data, meta }`.
 *
 * Three list shapes exist on the wire (FRONTEND_PLAN §1.1) and the caller chooses how to read them:
 *   1. `{ data: T }`                                       → unwrap()
 *   2. `{ data: T[], meta: { cursor, hasMore, pageSize } }` → unwrapPage()
 *   3. `{ data: { items: T[] } }`                           → unwrapItems()
 * A raw CSV body is not an envelope and is handled by the client's `requestBlob`.
 */
import type { PaginationMeta, SuccessEnvelope } from './types'

export interface Unwrapped<T> {
  readonly data: T
  readonly meta: Readonly<Record<string, unknown>> | undefined
}

export interface Page<T> {
  readonly items: readonly T[]
  readonly meta: PaginationMeta
}

export function isSuccessEnvelope(value: unknown): value is SuccessEnvelope<unknown> {
  return typeof value === 'object' && value !== null && 'data' in value
}

export function unwrap<T>(body: unknown): Unwrapped<T> {
  if (!isSuccessEnvelope(body)) {
    throw new TypeError('Response body is not a { data } envelope')
  }
  return { data: body.data as T, meta: body.meta }
}

export function isPaginationMeta(value: unknown): value is PaginationMeta {
  if (typeof value !== 'object' || value === null) return false
  const meta = value as Partial<PaginationMeta>
  return (
    (typeof meta.cursor === 'string' || meta.cursor === null) &&
    typeof meta.hasMore === 'boolean' &&
    typeof meta.pageSize === 'number'
  )
}

export function unwrapPage<T>(body: unknown): Page<T> {
  const { data, meta } = unwrap<readonly T[]>(body)
  if (!Array.isArray(data)) throw new TypeError('Paginated response `data` is not an array')
  if (!isPaginationMeta(meta))
    throw new TypeError('Paginated response has no { cursor, hasMore, pageSize } meta')
  return { items: data, meta }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T names the caller's item type
export function unwrapItems<T>(body: unknown): readonly T[] {
  const { data } = unwrap<{ readonly items?: unknown }>(body)
  if (typeof data !== 'object' || data === null || !Array.isArray(data.items)) {
    throw new TypeError('Response `data.items` is not an array')
  }
  return data.items as readonly T[]
}
